import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home, LifeBuoy } from "lucide-react";

/**
 * RedeemErrorBoundary
 *
 * Catches ANY runtime crash inside the Redeem flow so players never see a
 * black/blank page. Shows the player a friendly recovery card with:
 *   - "Try again" (resets boundary state)
 *   - "Hard reload" (clears cache + reloads)
 *   - "Back to Lobby"
 *   - "Contact Support" link
 *
 * Also surfaces the underlying error message so we can SEE what's crashing
 * (vs. an opaque black screen).
 */
class RedeemErrorBoundary extends Component {
  state = { hasError: false, errorMsg: "" };

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMsg: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[RedeemErrorBoundary] caught:", error, errorInfo);
    // Best-effort: post to backend so the admin can see how many players hit it.
    try {
      const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/client-errors";
      // Try to attach the logged-in user_id + email so admins can correlate
      // crashes to support tickets (set by App.js on login).
      let user_id = null, username = null, email = null;
      try {
        const raw = sessionStorage.getItem("jr_user") || localStorage.getItem("jr_user");
        if (raw) {
          const u = JSON.parse(raw);
          user_id = u?.id || null;
          username = u?.username || null;
          email = u?.email || null;
        }
      } catch {}
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: "redeem",
          message: (error && error.message) || String(error),
          stack: (error && error.stack ? String(error.stack).slice(0, 2000) : ""),
          component_stack: errorInfo?.componentStack ? String(errorInfo.componentStack).slice(0, 2000) : "",
          ua: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          url: window.location.href,
          user_id,
          username,
          email,
          ts: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }

  retry = () => {
    this.setState({ hasError: false, errorMsg: "" });
  };

  hardReload = () => {
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister().catch(() => {}));
        }).catch(() => {});
      }
      if (window.caches && window.caches.keys) {
        window.caches.keys().then((keys) => {
          keys.forEach((k) => window.caches.delete(k).catch(() => {}));
        }).catch(() => {});
      }
    } catch {}
    const sep = window.location.search.includes("?") ? "&" : "?";
    window.location.replace(window.location.pathname + window.location.search + sep + "_v=" + Date.now());
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[80vh] flex items-center justify-center bg-[#050505] p-4">
          <div className="max-w-md w-full text-center bg-[#0f1118] border border-amber-500/30 rounded-2xl p-6 sm:p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500/15 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white mb-2">
              Redeem page hit a snag
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              Something went wrong loading the Redeem page. This is on us — we've been notified and the team is on it.
            </p>
            <div className="bg-black/40 border border-white/10 rounded-lg p-2.5 mb-5 text-[11px] text-zinc-500 font-mono break-words max-h-24 overflow-y-auto">
              {this.state.errorMsg || "(no error message)"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <button
                onClick={this.retry}
                className="h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center justify-center gap-1.5 text-sm"
                data-testid="redeem-error-retry"
              >
                <RefreshCw className="w-4 h-4" /> Try Again
              </button>
              <button
                onClick={this.hardReload}
                className="h-11 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold flex items-center justify-center gap-1.5 text-sm"
                data-testid="redeem-error-reload"
              >
                <RefreshCw className="w-4 h-4" /> Hard Reload
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 text-xs">
              <Link to="/lobby" className="text-zinc-400 hover:text-white flex items-center gap-1">
                <Home className="w-3.5 h-3.5" /> Back to Lobby
              </Link>
              <Link to="/support" className="text-zinc-400 hover:text-white flex items-center gap-1">
                <LifeBuoy className="w-3.5 h-3.5" /> Contact Support
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default RedeemErrorBoundary;
